/**
 * Import history (SPEC-AUDITOR-CSV-IMPORT.md section 7): when, who, filename,
 * counts -- newest first, so a missed day is visible at a glance. Rendered
 * only for reports:import holders; the panel reloads after every apply.
 */
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';

import type { ReportImportRow } from '../../api/index.ts';
import { EmptyRow } from '../../components/EmptyRow.tsx';
import { LoadErrorAlert } from '../../components/LoadErrorAlert.tsx';
import { LoadingRow } from '../../components/LoadingRow.tsx';
import { formatLocalDateTime } from '../dashboard/dashboardHelpers.ts';
import { importCountsPhrase, importedByLabel } from './importSummary.ts';

const COL_SPAN = 5;

/** report_imports columns map onto the same phrase the verdict line uses. */
function rowCountsPhrase(row: ReportImportRow): string {
  return importCountsPhrase({
    total: row.rows_total,
    added: row.rows_added,
    updated: row.rows_updated,
    unchanged: row.rows_unchanged,
    skippedSync: row.rows_skipped_sync,
    flagged: row.rows_flagged,
  });
}

export interface ImportHistoryPanelProps {
  imports: ReportImportRow[];
  loading: boolean;
  error: string | null;
}

export function ImportHistoryPanel({ imports, loading, error }: Readonly<ImportHistoryPanelProps>): React.JSX.Element {
  return (
    <Stack spacing={1}>
      <Typography variant="h6">Import history</Typography>
      {error !== null && <LoadErrorAlert message={error} />}
      <Paper sx={{ width: '100%' }}>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>When</TableCell>
                <TableCell>Who</TableCell>
                <TableCell>File</TableCell>
                <TableCell align="right">Rows</TableCell>
                <TableCell>Result</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={COL_SPAN}>
                    <LoadingRow label="Loading import history..." />
                  </TableCell>
                </TableRow>
              )}
              {!loading && imports.length === 0 && <EmptyRow colSpan={COL_SPAN} message="No imports yet." />}
              {!loading &&
                imports.map((row) => (
                  <TableRow key={row.id} hover>
                    <TableCell>{formatLocalDateTime(row.imported_at)}</TableCell>
                    <TableCell>{importedByLabel(row.user_display_name, row.user_email)}</TableCell>
                    <TableCell>{row.filename}</TableCell>
                    <TableCell align="right">{row.rows_total}</TableCell>
                    <TableCell>{rowCountsPhrase(row)}</TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Stack>
  );
}

export default ImportHistoryPanel;
